/** @file Uploads the chosen layers one at a time (bottom to top), then places them with image.importLayers. */
import { names } from '@pose2d/format/names';
import type { CommandCall, Warning } from '@pose2d/format/types';
import type { ImportOutcome, ImportPorts, ImportProgress, ImportRun, LayerChoice, UploadedLayer } from './importTypes';

/** image.importLayers takes at most 256 layers; a longer list becomes several calls in one batch, bottom first. */
const PER_CALL = 256;
/** `origin.psd` of a placed PNG import (provenance only, spec §5.2). */
const PNG_SET = 'png-import';

function placementCalls(run: ImportRun, chosen: readonly LayerChoice[], uploaded: ReadonlyMap<number, UploadedLayer>): CommandCall[] {
  const psd = run.source.kind === 'psd' ? run.source.fileName : PNG_SET;
  const layers = chosen.map((c) => ({ image: uploaded.get(c.index)?.imageId ?? c.name, path: c.path, x: c.x, y: c.y }));
  const calls: CommandCall[] = [];
  for (let i = 0; i < layers.length; i += PER_CALL) calls.push({ name: 'image.importLayers', input: { psd, layers: layers.slice(i, i + PER_CALL) } });
  return calls;
}

function layerPng(run: ImportRun, index: number): Promise<Uint8Array> {
  if (run.source.kind === 'psd') return run.source.session.layerPng(index);
  const file = run.source.files[index];
  return file === undefined ? Promise.reject(new Error(`no PNG file ${String(index)}`)) : Promise.resolve(file.png);
}

/** Uploads every chosen layer `run.uploaded` lacks, reporting each one, and returns the full uploaded map. */
async function uploadAll(run: ImportRun, ports: ImportPorts, projectId: string, progress: (p: ImportProgress) => void): Promise<Map<number, UploadedLayer>> {
  const chosen = run.choices.filter((c) => c.checked);
  const uploaded = new Map(run.uploaded);
  progress({ projectId, uploaded: new Map(uploaded), total: chosen.length });
  for (const choice of chosen) {
    if (uploaded.has(choice.index)) continue;
    const up = await ports.uploadImage(projectId, choice.name, await layerPng(run, choice.index));
    uploaded.set(choice.index, { imageId: up.imageId, name: up.name, rev: up.rev });
    progress({ projectId, uploaded: new Map(uploaded), total: chosen.length });
  }
  return uploaded;
}

/** A PSD import always places, a PNG import only with `place`; nothing checked sends no batch (`min(1)`, W3-76). */
const placesLayers = (run: ImportRun, chosen: readonly LayerChoice[]): boolean => chosen.length > 0 && (run.source.kind === 'psd' || run.place);

/**
 * Resumable: layers in `run.uploaded` are not sent again, and a new project is created only once (the progress
 * report carries its id). Ends with one image.importLayers batch when it places, then waits for `ports.confirmed` of
 * the last commit (W3-72), so the outcome is reported once the editor's document holds the import.
 */
export async function runImport(run: ImportRun, ports: ImportPorts, progress: (p: ImportProgress) => void): Promise<ImportOutcome> {
  const projectId = run.target.kind === 'project' ? run.target.projectId : (await ports.createProject(run.target.name)).id;
  const uploaded = await uploadAll(run, ports, projectId, progress);
  const chosen = run.choices.filter((c) => c.checked);
  const warnings: Warning[] = run.source.kind === 'psd' ? [...run.source.session.warnings] : [];
  let rev = [...uploaded.values()].reduce((max, u) => Math.max(max, u.rev), 0);
  if (placesLayers(run, chosen)) {
    const applied = await ports.applyCommands(projectId, placementCalls(run, chosen, uploaded));
    rev = applied.rev;
    for (const w of applied.warnings) warnings.push(w);
  }
  if (rev > 0) await ports.confirmed(rev);
  const changed = chosen.filter((c) => uploaded.get(c.index)?.name !== c.name);
  // A valid name that changed was taken (the server added `-<n>`); any other changed name was slugified to ASCII.
  const deduplicated = changed.filter((c) => names.isValid('image', c.name)).length;
  return { projectId, images: chosen.length, renamed: changed.length - deduplicated, deduplicated, rev, warnings };
}
